/** @jsxImportSource @emotion/react */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import ImageBlock from '@component/common/block/image-block';
import Gap from '@component/common/gap';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import TextButton from '@design-system/text-button';
import { IAuthenticExpressionToastData } from '@datatype/iyagigo/type';
import { ETheme } from '@datatype/type';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as classroomActions } from '@store/slice/classroom-slice';
import { actions as modalActions } from '@store/slice/modal-slice';
import { useNavigate } from 'react-router-dom';
import { createClipPath } from '@constant/iyagigo/route-path';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';

import { useExpressionsQuery } from '@hook/iyagigo/useApi';
import { useInfiniteScroll } from '@hook/common/useScroll';
import { v4 as uuid4 } from 'uuid';
import { completeSentence, markBlanks } from '@util/string';

interface IProps {
  data: IAuthenticExpressionToastData['data'];
}

const Wrapper = styled.div`
  position: relative;
  height: ${size.TOAST_HEIGHT};
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
`;

const ExpressionsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 10px;
  width: 100%;
`;

const ExpressionItemContainer = styled.div<{ $selected?: boolean }>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  border-radius: 5px;
  border: 1px solid ${(props) => (props.$selected ? color.BROWN_2 : color.GRAY_BOX_BORDER_COLOR)};
  padding: 5px 10px 5px 5px;
  gap: ${size.MARGIN_1};
  width: 100%;
  cursor: pointer;
`;

const ExpressionItemDescriptionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex: 1;
  min-width: 0;
`;

const AuthenticExpressionToast: React.FC<IProps> = ({ data }) => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;

  const expressionQuery = useExpressionsQuery(data.languageCode, data.releaseDate);
  // 첫 번째 아이템은 clip-home의 "오늘의 표현" 카드와 동일하므로 제외
  const expressions = useMemo(
    () => (expressionQuery.data?.pages.flatMap((page) => page.data ?? []) ?? []).slice(1),
    [expressionQuery.data],
  );

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: expressionQuery.hasNextPage ?? false,
    isFetchingNextPage: expressionQuery.isFetchingNextPage ?? false,
    fetchNextPage: expressionQuery.fetchNextPage,
    root: scrollContainerRef,
  });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    if (selectedKey === null && expressions[0]) {
      setSelectedKey(expressions[0].key);
    }
  }, [expressions, selectedKey]);

  const handleSelect = (key: string) => {
    setSelectedKey(key);
  };

  const handleStudyClick = () => {
    if (!selectedKey) return;
    dispatch(classroomActions.storeCurrentAuthenticExpressionKey(selectedKey));
    dispatch(modalActions.hideToast());
    navigate(createClipPath());
  };

  const Expressions = expressions.map((expression) => {
    const isSelected = selectedKey === expression.key;
    const keyExpressionTemplate = expression?.sourceKeySegment?.keyExpressionTemplate || '';
    const keyExpressionAnswer = expression?.sourceKeySegment?.keyExpressionAnswer || [];
    const keyExpressionPronunciation = expression?.sourceKeySegment?.keyExpressionPronunciation || '';
    const markedText = markBlanks(completeSentence(keyExpressionTemplate, keyExpressionAnswer, true));

    return (
      <ExpressionItemContainer key={uuid4()} $selected={isSelected} onClick={() => handleSelect(expression.key)}>
        <ImageBlock
          src={expression?.authenticExpressionSource?.imageLink || ''}
          alt='past-authentic-expression-thumbnail'
          design={{
            image: { height: '50px' },
            box: { borderRadius: size.BORDER_RADIUS_2 },
          }}
        />
        <ExpressionItemDescriptionContainer>
          <TextBlock
            text={markedText}
            annotation={
              studyLanguage === EApiLanguageCode.JAPANESE && keyExpressionPronunciation
                ? { reading: keyExpressionPronunciation }
                : undefined
            }
            annotationLayout='start'
            blank={{ underlineColor: color.BLACK_2 }}
            design={{
              text: { size: 'medium', color: color.BLACK_2, bold: isSelected, oneline: true },
              box: { alignX: 'left' },
            }}
          />
          <TextBlock
            text={expression?.keySegmentKo?.keyExpressionTranslation || ''}
            design={{
              text: { size: 'small', color: color.GRAY_1, bold: false, oneline: true },
              box: { alignX: 'left' },
            }}
          />
        </ExpressionItemDescriptionContainer>
      </ExpressionItemContainer>
    );
  });

  return (
    <Wrapper>
      {/*<TextBlock*/}
      {/*  text='표현 더보기'*/}
      {/*  design={{*/}
      {/*    text: { size: 'large', color: color.BLACK_2, bold: true },*/}
      {/*    box: { alignX: 'left', width: '100%' },*/}
      {/*  }}*/}
      {/*/>*/}
      {/*<Gap height={'5px'} />*/}
      <ScrollContainer>
        <Scroll direction='y' height='100%' containerRef={scrollContainerRef}>
          <ExpressionsContainer>
            {Expressions}
            {/* 무한스크롤 sentinel — viewport 진입 시 다음 페이지 자동 fetch */}
            {expressions.length > 0 && <div ref={sentinelRef} style={{ width: '100%', height: '1px' }} />}
          </ExpressionsContainer>
        </Scroll>
      </ScrollContainer>
      <Gap height={size.MARGIN_1} />
      <TextButton
        text={'학습하기'}
        theme={ETheme.BROWN}
        size={'medium'}
        shape='pill'
        width={'100%'}
        handleClick={handleStudyClick}
        disabled={!selectedKey}
      />
    </Wrapper>
  );
};

export default AuthenticExpressionToast;
